import React, { memo } from 'react';
import { Box, Button, CircularProgress } from '@mui/material';
import { Save as SaveIcon, Delete as DeleteIcon } from '@mui/icons-material';

interface FormActionBarBaseProps {
  saving: boolean;
  saveDisabled?: boolean;
  saveLabel?: string;
  savingLabel?: string;
  /** Size for buttons — default undefined (medium) */
  size?: 'small' | 'medium';
  /** Show delete button */
  onDelete?: () => void;
  deleteLabel?: string;
  deleteDisabled?: boolean;
  /** Extra content between delete and save (e.g. publish button, status text) */
  children?: React.ReactNode;
  /** Override container sx */
  sx?: Record<string, unknown>;
}

// `hideSave` hides the save button entirely; a bar that always hides it needs no save handler.
type FormActionBarProps = FormActionBarBaseProps & (
  | { onSave: () => void; hideSave?: boolean }
  | { onSave?: undefined; hideSave: true }
);

const FormActionBar: React.FC<FormActionBarProps> = ({
  onSave,
  saving,
  saveDisabled,
  saveLabel = 'Save',
  savingLabel = 'Saving...',
  size,
  onDelete,
  deleteLabel = 'Delete',
  deleteDisabled,
  children,
  sx: sxProp,
  hideSave,
}) => (
  <Box
    sx={{
      display: 'flex',
      justifyContent: 'space-between',
      alignItems: 'center',
      pt: 2,
      mt: 2,
      borderTop: 1,
      borderColor: 'divider',
      ...sxProp,
    }}
  >
    <Box>
      {onDelete && (
        <Button
          size={size}
          variant="outlined"
          color="error"
          startIcon={<DeleteIcon />}
          onClick={onDelete}
          disabled={deleteDisabled ?? saving}
        >
          {deleteLabel}
        </Button>
      )}
    </Box>
    <Box sx={{ display: 'flex', gap: 1, alignItems: 'center' }}>
      {children}
      {!hideSave && (
        <Button
          size={size}
          variant="contained"
          startIcon={saving ? <CircularProgress size={size === 'small' ? 14 : 16} /> : <SaveIcon />}
          onClick={onSave}
          disabled={saving || saveDisabled}
        >
          {saving ? savingLabel : saveLabel}
        </Button>
      )}
    </Box>
  </Box>
);

export default memo(FormActionBar);
